import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Sidebar } from '@/components/Sidebar'
import { MessageList } from '@/components/MessageList'
import { ChatInput } from '@/components/ChatInput'
import { SettingsDrawer } from '@/components/SettingsDrawer'
import { AdminModal } from '@/components/AdminModal'
import { AuthConfig, HealthResponse, Conversation, Message, User, ThinkingStep } from '@/lib/types'
import { apiClient, TokenExpiredError } from '@/lib/api'
import { useToast } from '@/hooks/use-toast'
import { UserProfileDropdown } from '@/components/UserProfileDropdown'
import { SessionExpiredDialog } from '@/components/SessionExpiredDialog'
import { authService } from '@/lib/auth'
import { 
  getSystemPrompt, 
  setSystemPrompt as saveSystemPrompt, 
  storage, 
  storageKeys,
} from '@/lib/storage'
import { 
  Menu, 
  Settings, 
  LogOut
} from 'lucide-react'

interface ChatLayoutProps {
  authConfig: AuthConfig
  healthStatus: HealthResponse
  onLogout: () => void
}

export function ChatLayout({ 
  authConfig, 
  healthStatus, 
  onLogout 
}: ChatLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [adminModalOpen, setAdminModalOpen] = useState(false)
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [isStreaming, setIsStreaming] = useState(false)
  const [systemPrompt, setSystemPrompt] = useState<string>('')
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  // SharePoint / Giatec Knowledge toggle: default OFF per login/session
  const [sharepointSearchEnabled, setSharepointSearchEnabledState] = useState<boolean>(false)
  // Image Generator toggle: default OFF per message
  const [imageGeneratorEnabled, setImageGeneratorEnabled] = useState<boolean>(false)
  // Web Parser toggle: default OFF per message
  const [webParserEnabled, setWebParserEnabled] = useState<boolean>(false)
  const [showSessionExpired, setShowSessionExpired] = useState(false)
  const [pendingModelId, setPendingModelId] = useState<string | null>(null) // Store model selection for new conversations
  const { toast } = useToast()
  const navigate = useNavigate()
  const isMountedRef = useRef(true)
  const abortControllerRef = useRef<AbortController | null>(null)

  const handleTokenExpiration = useCallback(async () => {
    console.log('[ChatLayout] handleTokenExpiration called, showing session expired dialog...')
    // Show the session expired dialog with countdown
    setShowSessionExpired(true)
  }, [])

  const loadCurrentUser = useCallback(async () => {
    try {
      const user = await apiClient.getCurrentUser()
      setCurrentUser(user)
    } catch (error) {
      // Handle token expiration - redirect to login page
      if (error instanceof TokenExpiredError) {
        console.log('[ChatLayout] TokenExpiredError caught in loadCurrentUser, redirecting...')
        await handleTokenExpiration()
        return
      }
      
      console.error('Failed to load current user:', error)
      // Don't show toast for user loading failure as it's not critical
    }
  }, [handleTokenExpiration])

  const loadConversations = useCallback(async () => {
    try {
      const data = await apiClient.getConversations()
      setConversations(data)
    } catch (error) {
      // Handle token expiration - redirect to login page
      if (error instanceof TokenExpiredError) {
        await handleTokenExpiration()
        return
      }
      
      console.error('Failed to load conversations:', error)
      toast({
        title: "Error",
        description: "Failed to load conversations",
        variant: "destructive",
      })
    }
  }, [toast, handleTokenExpiration])

  useEffect(() => {
    loadConversations()
    loadCurrentUser()
    
    // Load saved system prompt from localStorage
    const savedPrompt = getSystemPrompt()
    if (savedPrompt) {
      setSystemPrompt(savedPrompt)
    }
    // If there is a draft message from template, start fresh conversation context
    const draft = storage.getItem<{ content: string; title?: string }>(storageKeys.DRAFT_MESSAGE, null)
    if (draft && draft.content) {
      toast({ title: 'Template loaded', description: draft.title ? `Editing: ${draft.title}` : 'You can edit the pre-filled content.' })
      setCurrentConversation(null)
      setMessages([])
    }
    
    return () => {
      isMountedRef.current = false
    }
  }, [loadConversations, loadCurrentUser])

  const loadMessages = async (conversationId: number) => {
    try {
      // Don't reload messages if we're currently streaming - preserve thinking state
      if (isStreaming) {
        console.log('⏸️ Skipping loadMessages during streaming to preserve thinking state')
        return
      }
      
      const conversation = await apiClient.getConversation(conversationId)
        
        // Load messages from the conversation object if it has them
        if (conversation.messages && conversation.messages.length > 0) {
        // Return messages as-is (thinking steps are not persisted, only shown during streaming)
        setMessages(conversation.messages)
        } else {
        setMessages([])
        }
    } catch (error) {
      // Handle token expiration - redirect to login page
      if (error instanceof TokenExpiredError) {
        await handleTokenExpiration()
        return
      }
      
      console.error('Failed to load messages:', error)
      toast({
        title: "Error",
        description: "Failed to load messages",
        variant: "destructive",
      })
    }
  }

  const handleNewConversation = () => {
    setCurrentConversation(null)
    setMessages([])
    setPendingModelId(null) // Clear pending model when starting new conversation
    // For each new conversation, default SharePoint / Giatec Knowledge to OFF
      setSharepointSearchEnabledState(false)
      // Reset image generator toggle for each new conversation
      setImageGeneratorEnabled(false)
      // Reset web parser toggle for each new conversation
      setWebParserEnabled(false)
  }

  const handleSelectConversation = async (conversationId: number) => {
    const conversation = conversations.find(c => c.id === conversationId)
    if (conversation) {
      setCurrentConversation(conversation)
      // Load messages for this conversation
      await loadMessages(conversationId)
    }
  }

  const handleFileUpload = async (files: File[]) => {
    try {
      const uploadedFiles = await Promise.all(
        files.map(async (file) => {
          const response = await apiClient.uploadFile(file)
          return response
        })
      )
      
      toast({
        title: "Files uploaded",
        description: `${files.length} file(s) uploaded successfully`,
      })
      
      return uploadedFiles
    } catch (error) {
      // Handle token expiration - redirect to login page
      if (error instanceof TokenExpiredError) {
        await handleTokenExpiration()
        throw error
      }

      throw error
    }
  }

  const handleStopGeneration = useCallback(() => {
    if (abortControllerRef.current) {
      console.log('[ChatLayout] Stopping generation...')
      abortControllerRef.current.abort()
      abortControllerRef.current = null
      setIsStreaming(false)
      toast({
        title: "Generation stopped",
        description: "AI response generation has been stopped.",
      })
    }
  }, [toast])

  const handleSendMessage = async (content: string, attachments?: any[]) => {
    console.log('handleSendMessage called with:', { content, attachments })
    if (!content.trim() && !attachments?.length) return

    // Declare message variables outside try block to access in catch
    const tempUserId = Date.now() * 1000 + Math.floor(Math.random() * 1000)
    const userMessage: Message = {
      id: tempUserId,
      conversation_id: currentConversation?.id || 0,
      role: 'user',
      content: content || '[Attachment]',
      created_at: new Date().toISOString(),
      attachments: attachments,
    }
    const assistantMessage: Message = {
      id: tempUserId + 1,
      conversation_id: currentConversation?.id || 0,
      role: 'assistant',
      content: '',
      created_at: new Date().toISOString(),
    }

    try {
      setIsStreaming(true)
      
      // Create AbortController for this request
      const abortController = new AbortController()
      abortControllerRef.current = abortController
      
      // Add user message to UI immediately
      setMessages(prev => [...prev, userMessage])

      // Create assistant message placeholder
      console.log('🔥 ASSISTANT MESSAGE CREATED:', assistantMessage.id)
      setMessages(prev => [...prev, assistantMessage])

      // Stream response
      let fullContent = ''
      let thinkingSteps: ThinkingStep[] = [] // Store structured thinking steps
      // console.log('Sending message with conversation_id:', currentConversation?.id?.toString())
      
      // For existing conversations, only send the current user message
      // The backend will load the full conversation history from database
      const contentToSend = content || (attachments && attachments.length > 0 ? '[Attachment]' : '')
      const currentUserMessage = { 
        role: 'user', 
        content: contentToSend,
        attachments: attachments?.map(a => ({
          id: a.id,
          filename: a.filename,
          mime: a.mime,
          file_size: a.file_size,
          url: a.url
        }))
      }
      
      const messagesToSend = currentConversation 
        ? [currentUserMessage]  // Only current message for existing conversation
        : [
            ...messages.filter(m => m.id < 1000000000).map(m => ({ role: m.role, content: m.content })), // Only real messages (not temp ones)
            currentUserMessage
          ]  // All messages for new conversation
      
      const metadata: Record<string, any> = {}
      if (systemPrompt) {
        metadata.system_prompt_override = systemPrompt
      }

      // Always send the current SharePoint / Giatec Knowledge toggle state
      // so backend conversation.meta can be turned OFF as well as ON
      metadata.use_sharepoint_search = sharepointSearchEnabled
      
      // Send image generator toggle state to force image generation
      metadata.force_image_generation = imageGeneratorEnabled
      // Send web parser toggle state to force web parser
      metadata.force_web_parser = webParserEnabled

      // If this is a new conversation and user pre-selected a model, persist it on creation.
      // This ensures the very first assistant response uses the selected model.
      if (!currentConversation && pendingModelId) {
        metadata.main_model_id = pendingModelId
      }
      
      console.log('Sending message with sharepointSearchEnabled:', sharepointSearchEnabled)
      console.log('Sending message with imageGeneratorEnabled:', imageGeneratorEnabled)
      console.log('Final metadata to send:', metadata)
      
      const newConversationId = await apiClient.streamChat(
        {
          conversation_id: currentConversation?.id ? currentConversation.id.toString() : undefined,
          messages: messagesToSend,
          metadata: Object.keys(metadata).length > 0 ? metadata : undefined
        },
        (event) => {
          // Check if aborted before processing event
          if (abortController.signal.aborted) {
            return
          }
          
          // Debug: log all events to see what we're receiving
          if (event.type && (event.type.includes('thinking') || event.type === 'token' || event.type === 'status')) {
            console.log('📡 Stream event received:', event.type, event)
          }
          
          // Handle new structured thinking events
          if (event.type === 'thinking_step' && event.step) {
            console.log('✅ Received thinking_step event:', event.step)
            // Add new thinking step
            thinkingSteps = [...thinkingSteps, event.step]
            setMessages(prev => prev.map(m => {
              if (m.id === assistantMessage.id) {
                return {
                  ...m,
                  thinkingSteps: [...thinkingSteps],
                  content: fullContent || ''
                }
              }
              return m
            }))
          } else if (event.type === 'thinking_update' && event.step_id && event.delta) {
            // Update existing thinking step content
            thinkingSteps = thinkingSteps.map(step => {
              if (step.id === event.step_id) {
                return { ...step, content: step.content + event.delta }
              }
              return step
            })
            setMessages(prev => prev.map(m => {
              if (m.id === assistantMessage.id) {
                return {
                      ...m,
                  thinkingSteps: [...thinkingSteps],
                  content: fullContent || ''
                    }
                  }
                  return m
            }))
          } else if (event.type === 'thinking_complete' && event.step_id) {
            // Mark thinking step as completed
            thinkingSteps = thinkingSteps.map(step => {
              if (step.id === event.step_id) {
                return { ...step, status: 'completed' as const }
              }
              return step
            })
            setMessages(prev => prev.map(m => {
              if (m.id === assistantMessage.id) {
                return {
                  ...m,
                  thinkingSteps: [...thinkingSteps],
                  content: fullContent || ''
                }
              }
              return m
            }))
          } else if (event.type === 'token' && event.delta) {
            // Handle token stream - update content
            // Hide thinking steps when content becomes substantial (>100 chars)
            fullContent += event.delta
            const shouldHideThinking = fullContent.length > 100
            setMessages(prev => prev.map(m => {
              if (m.id === assistantMessage.id) {
                return {
                  ...m,
                  content: fullContent,
                  thinkingSteps: shouldHideThinking ? undefined : (thinkingSteps.length > 0 ? [...thinkingSteps] : undefined)
                }
              }
              return m
            }))
          } else if (event.type === 'status') {
            // Handle status messages - only show if no content and no thinking steps
            const statusMessage = event.data?.message || 'Processing...'
            if (fullContent.length === 0 && thinkingSteps.length === 0) {
              setMessages(prev => prev.map(m =>
                m.id === assistantMessage.id
                  ? { ...m, content: statusMessage }
                  : m
              ))
            }
          } else if (event.type === 'end') {
            setIsStreaming(false)
            abortControllerRef.current = null
            // Clear thinking steps when stream ends (answer is complete)
            // Only keep thinking if content is very short (might be an error or incomplete response)
            const shouldKeepThinking = fullContent.length < 50
            setMessages(prev => prev.map(m => {
              if (m.id === assistantMessage.id) {
                return {
                  ...m,
                  content: fullContent,
                  thinkingSteps: shouldKeepThinking ? m.thinkingSteps : undefined
                }
              }
              return m
            }))
            console.log('✅ STREAM ENDED: Answer complete, thinking steps cleared')
          } else if (event.type === 'error') {
            setIsStreaming(false)
            // Surface the error in the assistant message
            const errText = event.error || "Failed to get response"
            setMessages(prev => prev.map(m =>
              m.id === assistantMessage.id
                ? { ...m, content: errText, thinkingSteps: undefined }
                : m
            ))
            toast({
              title: "Error",
              description: errText,
              variant: "destructive",
            })
          }
        },
        abortController.signal
      )

      // If backend created a new conversation, update currentConversation
      if (!currentConversation && newConversationId) {
        // Refresh conversations and set current
        const updated = await apiClient.getConversations()
        setConversations(updated)
        const created = updated.find(c => c.id === Number(newConversationId))
        if (created) {
          setCurrentConversation(created)
          // update temp messages' conversation_id
          setMessages(prev => prev.map(m => ({ ...m, conversation_id: created.id })))
          // Clear pending model (it was applied during creation via metadata.main_model_id)
          if (pendingModelId) {
            setPendingModelId(null)
          }
        }
      } else if (currentConversation && newConversationId && currentConversation.id !== Number(newConversationId)) {
        // If we had a conversation but backend returned a different ID, update to the correct conversation
        const updated = await apiClient.getConversations()
        setConversations(updated)
        const correctConversation = updated.find(c => c.id === Number(newConversationId))
        if (correctConversation) {
          setCurrentConversation(correctConversation)
          // Reload messages for the correct conversation
          await loadMessages(Number(newConversationId))
        }
      } else {
        // Only reload conversations if we didn't just create a new one
        loadConversations()
      }

    } catch (error) {
      setIsStreaming(false)
      abortControllerRef.current = null
      
      // Don't show error if it was aborted by user
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('Stream aborted by user')
        // Update assistant message to show it was stopped
        setMessages(prev => prev.map(m => 
          m.id === assistantMessage.id 
            ? { ...m, content: m.content || '[Stopped]' }
            : m
        ))
        return
      }
      
      console.error('Failed to send message:', error)
      
      // Handle token expiration - redirect to login page
      if (error instanceof TokenExpiredError) {
        await handleTokenExpiration()
        // Remove temporary messages
        setMessages(prev => prev.filter(m => m.id !== userMessage.id && m.id !== assistantMessage.id))
        return
      }
      
      // Handle specific error cases
      const errorMessage = error instanceof Error ? error.message : String(error)
      
      if (errorMessage.includes('not found') || errorMessage.includes('no longer available')) {
        // Conversation was deleted or no longer accessible
        toast({
          title: "Conversation Unavailable",
          description: "This conversation is no longer available. Starting a new conversation.",
          variant: "destructive",
        })
        
        // Clear current conversation and start fresh
        setCurrentConversation(null)
        setMessages([userMessage])  // Keep the user message they just sent
        loadConversations()  // Refresh conversation list
        
        // Retry the message in a new conversation after a delay
        const retryTimeout = setTimeout(() => {
          handleSendMessage(content, attachments)
        }, 1000)
        
        // Clean up timeout if component unmounts
        return () => clearTimeout(retryTimeout)
        
        return
      }
      
      // For other errors, show generic message
      toast({
        title: "Error",
        description: errorMessage.includes('rate limit') 
          ? "Rate limit exceeded. Please wait a moment and try again."
          : "Failed to send message. Please try again.",
        variant: "destructive",
      })
      
      // Remove the temporary messages on error
      setMessages(prev => prev.filter(m => m.id !== userMessage.id && m.id !== assistantMessage.id))
    }
  }

  const handleRenameConversation = async (conversationId: number, newTitle: string) => {
    if (!newTitle || !newTitle.trim()) {
      toast({
        title: "Error",
        description: "Conversation title cannot be empty",
        variant: "destructive",
      })
      return
    }

    try {
      const updatedConversation = await apiClient.updateConversation(conversationId, {
        title: newTitle.trim(),
      })
      
      // Update conversations list
      setConversations(prev => 
        prev.map(c => c.id === conversationId ? updatedConversation : c)
      )
      
      // Update current conversation if it's the one being renamed
      if (currentConversation?.id === conversationId) {
        setCurrentConversation(updatedConversation)
      }
      
      toast({
        title: "Success",
        description: "Conversation renamed",
      })
    } catch (error) {
      // Handle token expiration - redirect to login page
      if (error instanceof TokenExpiredError) {
        await handleTokenExpiration()
        return
      }
      
      console.error('Failed to rename conversation:', error)
      toast({
        title: "Error",
        description: "Failed to rename conversation",
        variant: "destructive",
      })
    }
  }

  const handleDeleteConversation = async (conversationId: number) => {
    try {
      await apiClient.deleteConversation(conversationId)
      setConversations(prev => prev.filter(c => c.id !== conversationId))
      
      if (currentConversation?.id === conversationId) {
        setCurrentConversation(null)
        setMessages([])
      }
      
      toast({
        title: "Success",
        description: "Conversation deleted",
      })
    } catch (error) {
      // Handle token expiration - redirect to login page
      if (error instanceof TokenExpiredError) {
        await handleTokenExpiration()
        return
      }
      
      console.error('Failed to delete conversation:', error)
      toast({
        title: "Error",
        description: "Failed to delete conversation",
        variant: "destructive",
      })
    }
  }

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-80' : 'w-0'} transition-all duration-300 overflow-hidden`}>
        <Sidebar
          conversations={conversations}
          selectedConversationId={currentConversation?.id}
          onSelectConversation={handleSelectConversation}
          onNewConversation={handleNewConversation}
          onDeleteConversation={handleDeleteConversation}
          onRenameConversation={handleRenameConversation}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* Header */}
        <div className="flex-shrink-0 border-b bg-gradient-to-r from-emerald-50 to-green-50 dark:from-gray-900 dark:to-gray-800 shadow-sm">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center space-x-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="hover:bg-white/50 dark:hover:bg-gray-700"
              >
                <Menu className="h-5 w-5" />
              </Button>
              
              {/* Giatec Logo */}
              <div className="flex items-center space-x-3">
                <img 
                  src="/giatec-logo.png" 
                  alt="Giatec" 
                  className="h-8 w-auto object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                  }}
                />
                <div className="h-6 w-px bg-gray-300 dark:bg-gray-600" />
                <h1 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                  {currentConversation?.title || 'AI Assistant'}
                </h1>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              {currentUser ? (
                <UserProfileDropdown
                  user={currentUser}
                  onLogout={onLogout}
                  onSettingsClick={() => setSettingsOpen(true)}
                  onAdminClick={currentUser.is_admin ? () => setAdminModalOpen(true) : undefined}
                />
              ) : (
                // Fallback buttons if user data hasn't loaded yet
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setSettingsOpen(true)}
                    className="hover:bg-white/50 dark:hover:bg-gray-700"
                  >
                    <Settings className="h-5 w-5" />
                  </Button>

                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={onLogout}
                    className="hover:bg-white/50 dark:hover:bg-gray-700"
                  >
                    <LogOut className="h-5 w-5" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-hidden min-h-0">
          <MessageList
            messages={messages}
            isStreaming={isStreaming}
          />
        </div>

        {/* Input */}
        <div className="flex-shrink-0 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <ChatInput
            onSendMessage={handleSendMessage}
            onFileUpload={handleFileUpload}
            disabled={isStreaming}
            placeholder="Type your message..."
            sharepointSearchEnabled={sharepointSearchEnabled}
            onSharepointSearchToggle={(enabled) => {
              setSharepointSearchEnabledState(enabled)
              toast({
                title: "Settings Saved",
                description: enabled 
                  ? "SharePoint search will be used for new conversations."
                  : "SharePoint search has been turned off for new conversations.",
              })
            }}
            imageGeneratorEnabled={imageGeneratorEnabled}
            onImageGeneratorToggle={(enabled) => {
              setImageGeneratorEnabled(enabled)
              toast({
                title: enabled ? "Image Generator Enabled" : "Image Generator Disabled",
                description: enabled 
                  ? "The next message will generate an image."
                  : "Image generation has been turned off.",
              })
            }}
            webParserEnabled={webParserEnabled}
            onWebParserToggle={(enabled) => {
              setWebParserEnabled(enabled)
              toast({
                title: enabled ? "Web Parser Enabled" : "Web Parser Disabled",
                description: enabled
                  ? "Web Parser will search websites to find answers."
                  : "Web Parser has been turned off.",
              })
            }}
            isStreaming={isStreaming}
            onStopGeneration={handleStopGeneration}
            conversationId={currentConversation?.id || null}
            currentModelId={currentConversation?.main_model_id || pendingModelId}
            onModelChange={async (modelId: string) => {
              if (currentConversation) {
                // Existing conversation: update immediately
                try {
                  await apiClient.updateConversationModel(currentConversation.id, modelId)
                  setCurrentConversation({
                    ...currentConversation,
                    main_model_id: modelId,
                  })
                } catch (error) {
                  console.error('Failed to update conversation model:', error)
                }
              } else {
                // New conversation: store pending model selection
                setPendingModelId(modelId)
                toast({
                  title: "Model Selected",
                  description: `Model set to ${modelId}. Will be applied when you send the first message.`,
                })
              }
            }}
          />
        </div>
      </div>

      {/* Settings Drawer */}
      <SettingsDrawer
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        authConfig={authConfig}
        healthStatus={healthStatus}
        systemPrompt={systemPrompt}
        onSystemPromptChange={(prompt) => {
          setSystemPrompt(prompt) // Update state
          saveSystemPrompt(prompt) // Save to localStorage
          toast({
            title: "Settings Saved",
            description: "System prompt updated. Will apply to new conversations.",
          })
        }}
      />

      {/* Admin Modal */}
      <AdminModal
        isOpen={adminModalOpen}
        onClose={() => setAdminModalOpen(false)}
        currentUser={currentUser}
      />

      {/* Session Expired Dialog */}
      {showSessionExpired && (
        <SessionExpiredDialog
          onNavigate={() => setShowSessionExpired(false)}
        />
      )}
    </div>
  )
}

